import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ShipService } from '../../../services/resources/ship.service';
import { first } from 'rxjs';
import { ShipInterface } from '../../../core/models';
import {
  CountryISO,
  NgxIntlTelInputModule,
  SearchCountryField,
} from 'ngx-intl-tel-input';

import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
@Component({
  selector: 'app-ship-editor',
  imports: [CommonModule, ReactiveFormsModule, NgxIntlTelInputModule],
  standalone: true,
  template: `
    <div class="mx-auto max-w-7xl pt-6 px-4 sm:px-6 lg:px-8">
      <form [formGroup]="shipForm" (ngSubmit)="onSubmit()" class="space-y-12">
        <div class="border-b border-gray-900/10 pb-12">
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            Ship Information
          </h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">
            Enter the details for the ship below.
          </p>

          <div class="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label
                for="companyName"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Company Name</label
              >
              <input
                id="companyName"
                type="text"
                formControlName="companyName"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>

            <div class="sm:col-span-3">
              <label
                for="shipName"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Shipname</label
              >
              <input
                id="shipName"
                type="text"
                formControlName="shipName"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
            <div class="sm:col-span-3">
              <label
                for="tel"
                class="block text-sm font-medium leading-6 text-gray-900 mb-2"
                >Reception Tel</label
              >
              <ngx-intl-tel-input
                [cssClass]="
                  'mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6'
                "
                [preferredCountries]="preferredCountries"
                [enableAutoCountrySelect]="true"
                [enablePlaceholder]="true"
                [searchCountryFlag]="true"
                [searchCountryField]="[
                  SearchCountryField.Iso2,
                  SearchCountryField.Name,
                ]"
                [selectFirstCountry]="false"
                [selectedCountryISO]="countryCode"
                [phoneValidation]="true"
                [separateDialCode]="true"
                formControlName="receptionTel"
              ></ngx-intl-tel-input>
              <div
                *ngIf="
                  shipForm.get('receptionTel')?.invalid &&
                  shipForm.get('receptionTel')?.touched
                "
                class="text-red-500 text-sm mt-1"
              >
                Valid phone number is required.
              </div>
            </div>
            <div class="sm:col-span-3">
              <label
                for="email"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Email Address</label
              >
              <input
                id="email"
                type="email"
                formControlName="shipEmail"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
            <div class="sm:col-span-3">
              <label
                for="shipDrRichardNr"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Dr. Richard Nr</label
              >
              <input
                id="shipDrRichardNr"
                type="number"
                formControlName="shipDrRichardNr"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="button"
            (click)="onCancel()"
            class="text-sm font-semibold leading-6 text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="shipForm.invalid"
            class="rounded-md bg-columbus-blue-600 disabled:opacity-25 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  `,
  styles: ``,
})
export class ShipEditorComponent {
  shipForm: FormGroup;
  shipService = inject(ShipService);
  id: string | null = null;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [
    CountryISO.Austria,
    CountryISO.Germany,
    CountryISO.Switzerland,
    CountryISO.Hungary,
    CountryISO.Slovakia,
  ];
  countryCode: CountryISO = CountryISO.Austria;
  PhoneNumberFormat = PhoneNumberFormat;
  constructor(
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
  ) {
    this.id = this.route.snapshot.paramMap.get('id');

    this.shipForm = this.fb.group({
      id: [''],
      companyName: ['', Validators.required],
      shipName: ['', Validators.required],
      receptionTel: [''],
      shipEmail: [],
      shipDrRichardNr: [],
    });
    if (this.id) {
      this.shipService
        .getShipData(this.id)
        .pipe(first())
        .subscribe((ship) => {
          if (ship) {
            this.patchForm(ship);
          }
        });
    }
  }
  patchForm(ship: ShipInterface) {
    this.shipForm.get('id')?.setValue(ship.id);
    this.shipForm.get('shipName')?.setValue(ship.shipName);
    this.shipForm.get('companyName')?.setValue(ship.companyName);
    if (ship.receptionTel) {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parse(ship.receptionTel, 'ZZ');
      const countryC = phoneUtil.getRegionCodeForNumber(number) as CountryISO;
      ship.receptionTel = number.getNationalNumber()?.toString() || '';
      this.countryCode = countryC;

      this.shipForm.get('receptionTel')?.setValue(ship.receptionTel);
    }
    if (ship.shipEmail) {
      this.shipForm.get('shipEmail')?.setValue(ship.shipEmail);
    }
    if (ship.shipDrRichardNr) {
      this.shipForm.get('shipDrRichardNr')?.setValue(ship.shipDrRichardNr);
    }
  }
  onSubmit() {
    if (this.shipForm.valid) {
      const ship: ShipInterface = this.shipForm.getRawValue();

      if (this.shipForm.get('receptionTel')?.value) {
        ship.receptionTel =
          this.shipForm.get('receptionTel')?.value.internationalNumber;
        // Format the phone number
        const phoneUtil = PhoneNumberUtil.getInstance();
        const number = phoneUtil.parse(ship.receptionTel, 'ZZ');
        ship.receptionTel = phoneUtil.format(
          number,
          PhoneNumberFormat.INTERNATIONAL,
        );
      }

      if (!this.id) {
        this.shipService.createNewShip(ship).then(() => {
          this.router.navigateByUrl('/ships');
        });
      } else {
        this.shipService.updateShip(ship).then(() => {
          this.router.navigateByUrl('/ships');
        });
      }
    }
  }

  onCancel() {
    this.shipForm.reset();
    this.router.navigateByUrl('/ships');
  }

  // Custom validator for phone number
  validatePhoneNumber(control: AbstractControl) {
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const number = phoneUtil.parse(control.value.internationalNumber, 'ZZ'); // 'ZZ' means international format
      return phoneUtil.isValidNumber(number)
        ? null
        : { invalidPhoneNumber: true };
    } catch (e) {
      return { invalidPhoneNumber: true };
    }
  }
}
